import { isEmptyOrNull } from '@/shared/utils/string';

// URL validation & normalization —————————————————————————————————————

const URL_HOST_REGEX =
  /^(https?:\/\/)?([\da-z]([\da-z-]*[\da-z])?\.)+[a-z]{2,}(\/\S*)?$/i;
const URL_HAS_SCHEME_REGEX = /^https?:\/\//i;

/** Empty is allowed (optional field); non-empty must look like a URL/host. */
export const isValidOrganizationUrl = (raw: string): boolean =>
  URL_HOST_REGEX.test(raw.trim());

/**
 * Backend requires urls to be empty or start with http:// or https://.
 * The form accepts bare domains ("www.example.com"); prepend https:// so
 * the payload passes server-side validation.
 */
export const normalizeUrlForPayload = (raw: string): string => {
  const trimmed = raw.trim();
  if (isEmptyOrNull(trimmed)) return '';
  if (URL_HAS_SCHEME_REGEX.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

// Phone validation ——————————————————————————————————————————————————

const PHONE_ALLOWED_REGEX = /^[\d\s()+/-]+$/;

/** Empty is allowed; non-empty must contain only phone-friendly characters. */
export const isValidOrganizationPhone = (raw: string): boolean =>
  PHONE_ALLOWED_REGEX.test(raw.trim());

// TerritorialAuthority facts — population / area ————————————————————————————————————
//
// Backend contract:
//   population   → unsigned int, `^\d+$`
//   areaSquareKm → positive decimal, up to 2 fractional digits, > 0

const POPULATION_REGEX = /^\d+$/;
const AREA_REGEX = /^\d+(\.\d{1,2})?$/;

const normalizeAreaInput = (raw: string): string =>
  raw
    .trim()
    .replaceAll(/[\s.](?=\d{3}\b)/g, '')
    .replace(',', '.');

/** Empty allowed; non-empty must be a non-negative integer (formatted input ok). */
export const isValidPopulation = (raw: string): boolean => {
  const trimmed = raw.trim();
  if (isEmptyOrNull(trimmed)) return true;
  const normalized = trimmed.replaceAll(/[\s,.]/g, '');
  return POPULATION_REGEX.test(normalized);
};

/** Empty allowed; non-empty must be a positive decimal with ≤ 2 fractional digits. */
export const isValidAreaSquareKm = (raw: string): boolean => {
  const trimmed = raw.trim();
  if (isEmptyOrNull(trimmed)) return true;
  const normalized = normalizeAreaInput(trimmed);
  if (!AREA_REGEX.test(normalized)) return false;
  return Number(normalized) > 0;
};

/** Format population for the update-request payload (bare digits). */
export const populationToPayload = (raw: string): string =>
  raw.trim().replaceAll(/[\s,.]/g, '');

/** Format area for the update-request payload (dot decimal separator). */
export const areaSquareKmToPayload = (raw: string): string =>
  normalizeAreaInput(raw);
